import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CLUB_TYPES, SKILL_LEVELS, PACE_RANGES, getPaceRangeDisplayLabel } from "../../constants/clubConstants";
import PaceUnitToggle from "./PaceUnitToggle";
import GooglePlacesAddressInput from "../GooglePlacesAddressInput";
import {
  ConsoleCarouselOrderStrip,
  ConsoleImageUrlPasteRow,
  renderInternalNotesField,
  renderSingleImageField,
} from "./ConsoleClubFormShared";
import { consoleApi } from "../../services/consoleApiService";
import { CLUB_EDITABLE_LINK_FIELDS, getApiErrorMessage, MAX_CAROUSEL_IMAGES, swapNeighbors } from "../../utils/consoleUtils";

/**
 * Console "Edit Club" dialog (club fields, carousel, scraped images).
 */
export default function ClubsEditClubModal({
  open,
  onRequestClose,
  editModalClub,
  editForm,
  setEditForm,
  editFormLoading,
  editSubmitting,
  onSubmit,
  editHeroFile,
  setEditHeroFile,
  editProfileFile,
  setEditProfileFile,
  editHeroPasteUrl,
  setEditHeroPasteUrl,
  editProfilePasteUrl,
  setEditProfilePasteUrl,
  editCarouselSlots,
  setEditCarouselSlots,
  editCarouselBlobById,
  editFullClub,
  setEditFullClub,
  editScrapedImageRemovingUrl,
  setEditScrapedImageRemovingUrl,
  setImagePreviewUrls,
  consolePaceUnit,
  refetchClubs,
  editPasteFieldSaving,
  onSaveHeroPasteUrl,
  onSaveProfilePasteUrl,
  onSaveCarouselPasteUrl,
}) {
  const [editCarouselPasteDraft, setEditCarouselPasteDraft] = useState("");
  useEffect(() => {
    setEditCarouselPasteDraft("");
  }, [open]);

  const addPastedCarouselUrl = () => {
    const u = editCarouselPasteDraft.trim();
    if (!u.startsWith("http")) return;
    setEditCarouselSlots((prev) => {
      if (prev.length >= MAX_CAROUSEL_IMAGES) return prev;
      if (
        prev.some(
          (s) =>
            (s.kind === "remote" || s.kind === "pasted_url") &&
            typeof s.url === "string" &&
            s.url.trim() === u
        )
      ) {
        return prev;
      }
      return [...prev, { id: `ecpu-${Date.now()}`, kind: "pasted_url", url: u }];
    });
    setEditCarouselPasteDraft("");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onRequestClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Club</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Update the club profile. Same fields as mobile; organizer account is unchanged. For profile, hero, and
            carousel you can <strong>paste HTTPS image URLs</strong> (imported to our storage when you save, or use{" "}
            <strong>Save</strong> next to a URL field to import without closing this dialog) in addition to file uploads
            and scraped-image buttons below.
          </p>
        </DialogHeader>
        {editFormLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading club…</div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  <span className="text-destructive">*</span> Required. Fields marked <span className="text-destructive">*</span> are required for the club to go public.
                </p>
                <div className="grid gap-2">
                  <label htmlFor="edit-run-club-name" className="text-sm font-medium">
                    Club name <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <Input
                    id="edit-run-club-name"
                    value={editForm.run_club_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, run_club_name: e.target.value }))}
                    placeholder="Club name"
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-city" className="text-sm font-medium">
                    City <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <Input
                    id="edit-city"
                    value={editForm.city}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, city: e.target.value }))}
                    placeholder="e.g. Paris"
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-state" className="text-sm font-medium">
                    State / region
                  </label>
                  <Input
                    id="edit-state"
                    value={editForm.state}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, state: e.target.value }))}
                    placeholder="e.g. FL, NY, or IDF (optional)"
                    autoComplete="off"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-country" className="text-sm font-medium">
                    Country
                  </label>
                  <Input
                    id="edit-country"
                    value={editForm.country}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, country: e.target.value }))}
                    placeholder="e.g. France, United States (optional)"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. For international clubs; often filled when you choose a meet-up address from Google Places.
                  </p>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-type" className="text-sm font-medium">
                    Type <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="edit-type"
                    value={editForm.type}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, type: e.target.value }))}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">—</option>
                    {CLUB_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-description" className="text-sm font-medium">
                    Description <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <Textarea
                    id="edit-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="About the club (max 1000 chars)"
                    rows={3}
                    maxLength={1000}
                    className="resize-none"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-website" className="text-sm font-medium">
                    Website
                  </label>
                  <Input
                    id="edit-website"
                    value={editForm.website}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, website: e.target.value }))}
                    placeholder="https://…"
                    autoComplete="off"
                    maxLength={CLUB_EDITABLE_LINK_FIELDS.website.maxLen}
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-instagram" className="text-sm font-medium">
                    Instagram URL
                  </label>
                  <Input
                    id="edit-instagram"
                    value={editForm.instagram}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, instagram: e.target.value }))}
                    placeholder="https://www.instagram.com/…"
                    autoComplete="off"
                    maxLength={CLUB_EDITABLE_LINK_FIELDS.instagram.maxLen}
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-skill-level" className="text-sm font-medium">
                    Who it&apos;s for (skill level) <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="edit-skill-level"
                    value={editForm.skill_level}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, skill_level: e.target.value }))}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">—</option>
                    {SKILL_LEVELS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-home-base-address" className="text-sm font-medium">
                    Meet-up address <span className="text-destructive" aria-hidden="true">*</span>
                  </label>
                  <GooglePlacesAddressInput
                    id="edit-home-base-address"
                    value={editForm.home_base_address}
                    onChange={(val) => setEditForm((prev) => ({ ...prev, home_base_address: val }))}
                    onPlaceSelected={(place) => {
                      const rawLat = place.latitude != null ? Number(place.latitude) : null;
                      const rawLon = place.longitude != null ? Number(place.longitude) : null;
                      const validCoords =
                        rawLat != null &&
                        rawLon != null &&
                        Number.isFinite(rawLat) &&
                        Number.isFinite(rawLon);
                      setEditForm((prev) => ({
                        ...prev,
                        home_base_address: place.formattedAddress ?? place.name ?? "",
                        city: place.city || prev.city,
                        state: place.state || prev.state,
                        country: (place.country && String(place.country).trim()) || prev.country,
                        latitude: validCoords ? rawLat : null,
                        longitude: validCoords ? rawLon : null,
                      }));
                    }}
                    placeholder="Address or place name"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-home-base-description" className="text-sm font-medium">
                    Meet-up description
                  </label>
                  <Textarea
                    id="edit-home-base-description"
                    value={editForm.home_base_description}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, home_base_description: e.target.value }))}
                    placeholder="e.g. We meet at the north entrance"
                    rows={2}
                    maxLength={500}
                    className="resize-none"
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-sm font-medium">Pace ranges (max 3)</label>
                    <PaceUnitToggle />
                  </div>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Pace ranges">
                    {PACE_RANGES.map((p) => {
                      const selected = Array.isArray(editForm.paces) && editForm.paces.includes(p.value);
                      const atMax = Array.isArray(editForm.paces) && editForm.paces.length >= 3;
                      return (
                        <Button
                          key={p.value}
                          type="button"
                          variant={selected ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const current = Array.isArray(editForm.paces) ? editForm.paces : [];
                            const next = current.includes(p.value)
                              ? current.filter((v) => v !== p.value)
                              : current.length < 3
                                ? [...current, p.value]
                                : current;
                            setEditForm((prev) => ({ ...prev, paces: next }));
                          }}
                          disabled={!selected && atMax}
                          className={selected ? "bg-[#1A1916] hover:bg-[#3C3830]" : ""}
                        >
                          {getPaceRangeDisplayLabel(p, consolePaceUnit)}
                        </Button>
                      );
                    })}
                  </div>
                  <span className="text-xs text-muted-foreground">Select up to 3 pace ranges (stored as min/mi)</span>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Club notes (max 3)</label>
                  {[0, 1, 2].map((i) => (
                    <Input
                      key={i}
                      id={`edit-club-note-${i}`}
                      value={editForm.club_notes?.[i] ?? ""}
                      onChange={(e) => {
                        const next = [...(editForm.club_notes ?? ["", "", ""])];
                        next[i] = e.target.value;
                        setEditForm((prev) => ({ ...prev, club_notes: next }));
                      }}
                      placeholder={`Note ${i + 1}`}
                      autoComplete="off"
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-recurring-runs-vary"
                    checked={editForm.recurring_runs_vary ?? false}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, recurring_runs_vary: e.target.checked }))}
                    className="rounded border-[var(--console-border)]"
                  />
                  <label htmlFor="edit-recurring-runs-vary" className="text-sm font-medium cursor-pointer">
                    Weekly runs vary (location or type)
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-is-anchor-club"
                    checked={editForm.is_anchor_club ?? false}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, is_anchor_club: e.target.checked }))}
                    className="rounded border-[var(--console-border)]"
                  />
                  <label htmlFor="edit-is-anchor-club" className="text-sm font-medium cursor-pointer">
                    Anchor club (one of the strongest, best-known in the city)
                  </label>
                </div>
                {renderInternalNotesField({
                  id: "edit-internal-notes",
                  value: editForm.internal_notes,
                  onChange: (val) => setEditForm((prev) => ({ ...prev, internal_notes: val })),
                })}
                {renderSingleImageField("Profile picture", {
                  currentUrl: editModalClub?.profile_picture ?? null,
                  file: editProfileFile,
                  onFileChange: setEditProfileFile,
                  onPreview: (url) => setImagePreviewUrls([url]),
                  inputId: "edit-profile-picture",
                })}
                <ConsoleImageUrlPasteRow
                  id="edit-profile-paste-url"
                  value={editProfilePasteUrl}
                  onChange={setEditProfilePasteUrl}
                  disabled={Boolean(editProfileFile)}
                  disabledHint="Remove the new profile file above to use a pasted URL instead."
                  hint="Replaces profile picture on save when no new file is chosen. Fetched and stored in our bucket."
                  labelClassName="text-xs font-medium text-[var(--console-muted)]"
                  inputClassName="flex h-9 w-full rounded-md border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-1 text-sm text-[var(--console-ink)] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onSave={onSaveProfilePasteUrl}
                  saveSaving={editPasteFieldSaving === "profile"}
                  saveDisabled={editSubmitting || editFormLoading}
                />
                {renderSingleImageField("Hero image", {
                  currentUrl: editModalClub?.hero_image ?? null,
                  file: editHeroFile,
                  onFileChange: setEditHeroFile,
                  onPreview: (url) => setImagePreviewUrls([url]),
                  inputId: "edit-hero-image",
                })}
                <ConsoleImageUrlPasteRow
                  id="edit-hero-paste-url"
                  value={editHeroPasteUrl}
                  onChange={setEditHeroPasteUrl}
                  disabled={Boolean(editHeroFile)}
                  disabledHint="Remove the new hero file above to use a pasted URL instead."
                  hint="Replaces hero on save when no new file is chosen. Fetched and stored in our bucket."
                  labelClassName="text-xs font-medium text-[var(--console-muted)]"
                  inputClassName="flex h-9 w-full rounded-md border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-1 text-sm text-[var(--console-ink)] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onSave={onSaveHeroPasteUrl}
                  saveSaving={editPasteFieldSaving === "hero"}
                  saveDisabled={editSubmitting || editFormLoading}
                />
                <div className="grid gap-2">
                  <label htmlFor="edit-carousel-images" className="text-sm font-medium text-[var(--console-ink)]">
                    Carousel images{" "}
                    <span className="text-[var(--console-muted)] font-normal">
                      (optional, max {MAX_CAROUSEL_IMAGES}; first slide is primary)
                    </span>
                  </label>
                  <p className="text-xs text-[var(--console-muted)]">
                    Reorder with arrows. Add files, paste HTTPS URLs (stored on our bucket on save), or use{" "}
                    <strong>Carousel</strong> on scraped images below, then <strong>Save changes</strong>.
                  </p>
                  <ConsoleCarouselOrderStrip
                    entries={editCarouselSlots.map((s, i) => ({
                      id: s.id,
                      previewSrc:
                        s.kind === "remote" || s.kind === "pasted_url"
                          ? s.url
                          : editCarouselBlobById[s.id] || "",
                      subtitle:
                        s.kind === "pasted_url"
                          ? "Pasted URL"
                          : s.kind === "remote"
                            ? `Slide ${i + 1}`
                            : (s.file?.name || "New file").slice(0, 24),
                    }))}
                    onMoveUp={(i) => setEditCarouselSlots((prev) => swapNeighbors(prev, i, -1))}
                    onMoveDown={(i) => setEditCarouselSlots((prev) => swapNeighbors(prev, i, 1))}
                    onRemove={(i) => setEditCarouselSlots((prev) => prev.filter((_, j) => j !== i))}
                    emptyHint="No carousel images yet — add files, paste a URL, or pick from scraped images."
                  />
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="edit-carousel-paste-url"
                      className="text-xs font-medium text-[var(--console-muted)]"
                    >
                      Paste image URL (https)
                    </label>
                    <div className="flex flex-wrap gap-2 items-end">
                      <Input
                        id="edit-carousel-paste-url"
                        type="url"
                        inputMode="url"
                        placeholder="https://…"
                        value={editCarouselPasteDraft}
                        onChange={(e) => setEditCarouselPasteDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addPastedCarouselUrl();
                          }
                        }}
                        disabled={editCarouselSlots.length >= MAX_CAROUSEL_IMAGES || editSubmitting}
                        className="flex-1 min-w-[12rem] border-[var(--console-border)] bg-[var(--console-surface)] text-[var(--console-ink)]"
                        autoComplete="off"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="tb-btn shrink-0"
                        disabled={editCarouselSlots.length >= MAX_CAROUSEL_IMAGES || editSubmitting}
                        onClick={addPastedCarouselUrl}
                      >
                        Add URL
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="tb-btn shrink-0"
                        disabled={
                          editCarouselSlots.length >= MAX_CAROUSEL_IMAGES ||
                          editSubmitting ||
                          editPasteFieldSaving === "carousel"
                        }
                        onClick={async () => {
                          if (typeof onSaveCarouselPasteUrl !== "function") return;
                          const ok = await onSaveCarouselPasteUrl(editCarouselPasteDraft);
                          if (ok) setEditCarouselPasteDraft("");
                        }}
                      >
                        {editPasteFieldSaving === "carousel" ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="edit-carousel-images"
                      className="cursor-pointer rounded border-0 bg-[#1A1916] px-3 py-1.5 text-white text-sm inline-block"
                    >
                      Choose Files
                    </label>
                    <input
                      id="edit-carousel-images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        setEditCarouselSlots((prev) => {
                          const room = MAX_CAROUSEL_IMAGES - prev.length;
                          if (room <= 0) return prev;
                          const next = [...prev];
                          let n = 0;
                          for (const file of picked) {
                            if (n >= room) break;
                            next.push({
                              id: `ecf-${Date.now()}-${n}-${file.name}`,
                              kind: "file",
                              file,
                            });
                            n += 1;
                          }
                          return next;
                        });
                        e.target.value = "";
                      }}
                      className="sr-only"
                      aria-label="Add carousel images"
                    />
                  </div>
                  {editCarouselSlots.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="tb-btn self-start"
                      onClick={() => {
                        const urls = editCarouselSlots
                          .map((s) =>
                            s.kind === "remote" || s.kind === "pasted_url"
                              ? s.url
                              : editCarouselBlobById[s.id]
                          )
                          .filter(Boolean);
                        if (urls.length) setImagePreviewUrls(urls);
                      }}
                    >
                      Preview carousel order
                    </Button>
                  )}
                </div>
                {editFullClub?.scraped_images?.length > 0 && (
                  <div className="grid gap-2">
                    <label className="text-sm font-medium text-[var(--console-ink)]">Imported / scraped images</label>
                    <p className="text-xs text-[var(--console-muted)]">
                      From Meetup import, Strava import, website scrape, or run-info extraction. Use as hero, profile, or carousel. Remove drops a URL from this pool only (not from hero/profile/carousel already saved on the club).
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {editFullClub.scraped_images.map((url, idx) => (
                        <div key={idx} className="flex flex-col items-center gap-1.5">
                          <img
                            src={url}
                            alt=""
                            className="h-20 w-20 object-cover rounded border border-[var(--console-border)] bg-[var(--console-surface)]"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div className="flex flex-wrap gap-1 justify-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={async () => {
                                try {
                                  await consoleApi.clubs.update(editModalClub.id, { hero_image: url });
                                  const updated = await consoleApi.clubs.get(editModalClub.id);
                                  setEditFullClub(updated);
                                  refetchClubs();
                                  toast.success("Hero image set from scraped image.");
                                } catch (e) {
                                  toast.error(getApiErrorMessage(e, "Failed to set hero image"));
                                }
                              }}
                            >
                              Hero
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={async () => {
                                try {
                                  await consoleApi.clubs.update(editModalClub.id, { profile_picture: url });
                                  const updated = await consoleApi.clubs.get(editModalClub.id);
                                  setEditFullClub(updated);
                                  refetchClubs();
                                  toast.success("Profile picture set from scraped image.");
                                } catch (e) {
                                  toast.error(getApiErrorMessage(e, "Failed to set profile picture"));
                                }
                              }}
                            >
                              Profile
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => {
                                setEditCarouselSlots((prev) => {
                                  if (prev.some((s) => s.kind === "remote" && s.url === url)) return prev;
                                  if (prev.length >= MAX_CAROUSEL_IMAGES) return prev;
                                  return [
                                    ...prev,
                                    {
                                      id: `ers-${Date.now()}-${idx}`,
                                      kind: "remote",
                                      url,
                                    },
                                  ];
                                });
                                toast.success("Added to carousel draft — Save changes to apply.");
                              }}
                            >
                              Carousel
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 text-[var(--console-red)] border-[var(--console-border)]"
                              disabled={!!editScrapedImageRemovingUrl}
                              aria-busy={editScrapedImageRemovingUrl === (typeof url === "string" ? url.trim() : "")}
                              onClick={async () => {
                                const stored = typeof url === "string" ? url : "";
                                const trimKey = stored.trim();
                                if (
                                  !window.confirm(
                                    "Remove this image URL from the club's scraped pool? It will no longer appear as a pick for runs or carousel."
                                  )
                                ) {
                                  return;
                                }
                                setEditScrapedImageRemovingUrl(trimKey);
                                try {
                                  const current = Array.isArray(editFullClub?.scraped_images) ? editFullClub.scraped_images : [];
                                  const next = current.filter((u) => u !== stored);
                                  await consoleApi.clubs.update(editModalClub.id, { scraped_images: next });
                                  const updated = await consoleApi.clubs.get(editModalClub.id);
                                  setEditFullClub(updated);
                                  setEditCarouselSlots((prev) =>
                                    prev.filter(
                                      (s) =>
                                        !(s.kind === "remote" && (typeof s.url === "string" ? s.url.trim() : "") === trimKey)
                                    )
                                  );
                                  refetchClubs();
                                  toast.success("Removed scraped image from club.");
                                } catch (e) {
                                  toast.error(getApiErrorMessage(e, "Failed to remove scraped image"));
                                } finally {
                                  setEditScrapedImageRemovingUrl(null);
                                }
                              }}
                            >
                              {editScrapedImageRemovingUrl === (typeof url === "string" ? url.trim() : "")
                                ? "Removing…"
                                : "Remove"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onRequestClose} disabled={editSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1A1916] hover:bg-[#3C3830]" disabled={editSubmitting}>
                {editSubmitting ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
